import { AttachmentFile } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { TaskDetailsScreenProps } from 'home/stack/types'
import { useCallback, useLayoutEffect, useState } from 'react'
import { confirmationAlert, errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { pickImageFromLibrary } from 'shared/image_picker'
import { openLink } from 'shared/utils'
import { useCompleteTask } from 'tasks/queries/complete'
import { useDeleteTask } from 'tasks/queries/delete'
import { useTask } from 'tasks/queries/detail'
import { RecurrentTaskUpdateType } from 'tasks/types'
import { editPhotoAttachment } from 'tasks/utils'
import { useUser } from 'users/queries/detail'
import { TaskDetailsView } from './views'

export function TaskDetailsScreen({
  navigation,
  route,
}: TaskDetailsScreenProps) {
  const { taskId, fromDate, toDate } = route.params
  const { theme } = useTheme()
  const userQuery = useUser()
  const markTaskCompleteMutation = useCompleteTask()
  const deleteTaskMutation = useDeleteTask()
  const taskDetailsQuery = useTask(taskId, { fromDate, toDate })
  const { handleNavigation } = useRoutingContext()
  const [photos, setPhotos] = useState<AttachmentFile[]>([])

  const isApprover = userQuery.data?.permissions?.canEditCustomTasks ?? false
  const isAssignee = taskDetailsQuery.data?.assignedTo.id === userQuery.data?.id
  const isCompleted = taskDetailsQuery.data?.occurrences[0]?.completed === true
  const occurrence =
    taskDetailsQuery.data?.occurrences &&
    taskDetailsQuery.data.occurrences.length > 0
      ? taskDetailsQuery.data.occurrences[0]
      : undefined

  const onPressEditPhoto = useCallback(
    async (photo: AttachmentFile) => {
      await editPhotoAttachment({
        photo,
        selectedPhotos: photos,
        setSelectedPhotos: setPhotos,
      })
    },
    [photos]
  )

  const onPressPhoto = useCallback(
    (url: string) => {
      handleNavigation({
        routeName: 'task_details_photo_viewer',
        routeParams: { taskId, fromDate, toDate, uri: url },
      })
    },
    [handleNavigation, taskId, fromDate, toDate]
  )

  const onDeleteTask = useCallback(
    async (updateType?: RecurrentTaskUpdateType) => {
      const confirmation = await confirmationAlert(
        t('deleteTaskTitle'),
        t('deleteTaskDescription')
      )
      if (!confirmation || !occurrence) {
        return
      }
      try {
        await deleteTaskMutation.mutateAsync({
          id: taskId,
          dueDate: occurrence.dueDate,
          exceptionId: occurrence.exceptionId,
          updateType,
        })
        handleNavigation({
          routeName: 'schedule',
          routeParams: { date: occurrence.dueDate },
        })
      } catch (e) {
        errorAlert(e, t('errorDeletingTask'))
      }
    },
    [deleteTaskMutation, handleNavigation, occurrence, taskId]
  )

  const onReassignTask = useCallback(() => {
    handleNavigation({
      routeName: 'task_user_list',
      routeParams: {
        taskId,
        fromDate,
        toDate,
        type: 'reassign',
      },
    })
  }, [handleNavigation, taskId, fromDate, toDate])

  const onEditTask = useCallback(() => {
    handleNavigation({
      routeName: 'edit_task',
      routeParams: {
        taskId,
        fromDate,
        toDate,
      },
    })
  }, [handleNavigation, taskId, fromDate, toDate])

  const onPressRemovePhoto = useCallback(
    (photo: AttachmentFile) => {
      setPhotos(photos.filter(item => photo.uri !== item.uri))
    },
    [photos]
  )

  const onPressAddPhoto = useCallback(async () => {
    const mediaResult = await pickImageFromLibrary({
      allowsMultipleSelection: true,
    })

    if (mediaResult.status === 'success') {
      const attachmentFiles: AttachmentFile[] = mediaResult.files
      setPhotos(prevState => [...prevState, ...attachmentFiles])
    }
  }, [])

  const onCompleteTask = useCallback(async () => {
    if (taskDetailsQuery.data && taskDetailsQuery.data.occurrences.length > 0) {
      const firstOccurrence = taskDetailsQuery.data.occurrences[0]
      try {
        await markTaskCompleteMutation.mutateAsync({
          taskId: firstOccurrence.taskId,
          due_date: firstOccurrence.dueDate,
          exception_id: firstOccurrence.exceptionId,
          files: photos,
        })
      } catch (e) {
        errorAlert(e, t('errorMarkingTaskComplete'))
      }
    }
  }, [markTaskCompleteMutation, photos, taskDetailsQuery.data])

  const onLinkPress = useCallback(
    async (url: string) => {
      if (userQuery.data) {
        await openLink(url, navigation, userQuery.data)
      }
    },
    [navigation, userQuery.data]
  )

  useLayoutEffect(() => {
    if (isApprover && !isCompleted) {
      showHeaderButton({
        navigation,
        title: t('editTask'),
        theme,
        onPress: onEditTask,
      })
    }
  }, [isApprover, isCompleted, navigation, theme, onEditTask])

  return (
    <StatusBoundary
      error={taskDetailsQuery.error}
      isLoading={taskDetailsQuery.isLoading}
    >
      <TaskDetailsView
        task={taskDetailsQuery.data}
        onCompleteTask={onCompleteTask}
        onPressRemovePhoto={onPressRemovePhoto}
        onPressEditPhoto={onPressEditPhoto}
        photos={photos}
        onPressAddPhoto={onPressAddPhoto}
        onDeleteTask={onDeleteTask}
        onReassignTask={onReassignTask}
        isApprover={isApprover}
        onPressPhoto={onPressPhoto}
        isAssignee={isAssignee}
        onLinkPress={onLinkPress}
      />
    </StatusBoundary>
  )
}
